/* Workers & register — how the month's present days are spread across
 * people, the workers to chase, ranked three ways, and what needs putting
 * right between the register and the biometric. */

import { useState } from 'react'
import type { AttMonth, AttWorker } from '../../lib/api_attendance'
import { PanelHead, Segmented } from '../../components/ui'
import { num } from '../../lib/format'
import type { Scope } from './model'
import { BAD, Foot, Sheet, Sub, Track, heads, words } from './parts'
import type { People } from './parts'

export type WorkerList = 'att' | 'miss' | 'late'
/** The misses and late lists rank by count or by share of present days. */
export type MissSort = 'n' | 'pct'
export interface WorkerUi { list: WorkerList; limit: number; missSort: MissSort; lateSort: MissSort }

interface Props {
  D: AttMonth; S: Scope; people: People
  ui: WorkerUi; setUi: (ui: WorkerUi) => void
}

export function WorkersTab(props: Props) {
  return (
    <>
      <Distribution {...props} />
      <ToChase {...props} />
      <Hygiene {...props} />
    </>
  )
}

/** Everyone in view with at least one present day — whole period, not
 *  date-filtered. */
const inView = (D: AttMonth, S: Scope) => D.workers.filter((w) => S.isAll || w.dept === S.sel)

// ------------------------------------------------------ distribution
/** A histogram of days present: how many people came in on 1 day, 2 days,
 *  ... up to the month. The bands — the top tenth of the month and under
 *  half of it — are the design's own cut. */
function Distribution({ D, S }: Props) {
  const [hov, setHov] = useState<number | null>(null)
  const ppl = inView(D, S).filter((w) => w.p > 0)
  const maxD = Math.max(D.dayCount || 0, ...ppl.map((w) => w.p))
  const cnt = Array.from({ length: maxD }, (_, i) => ppl.filter((w) => w.p === i + 1).length)
  const top = Math.max(1, ...cnt)
  const st = top <= 10 ? 2 : top <= 25 ? 5 : top <= 60 ? 10 : top <= 150 ? 25 : 50
  const ceil = Math.ceil(top / st) * st
  const full = Math.round(maxD * 0.9), low = Math.round(maxD * 0.5)
  const nFull = ppl.filter((w) => w.p >= full).length
  const nLow = ppl.filter((w) => w.p < low).length
  const sorted = ppl.map((w) => w.p).sort((a, b) => a - b)
  const med = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0

  const bars = cnt.map((c, i) => ({
    h: (c / ceil) * 100 + '%',
    xl: (i + 1) % 5 === 0 || i === 0 ? String(i + 1) : '',
    bg: hov === i ? 'var(--color-text)'
      : i + 1 >= full ? 'var(--color-accent-700)'
        : i + 1 < low ? 'var(--color-neutral-500)' : 'var(--color-accent-400)',
  }))
  const ticks = Array.from({ length: ceil / st + 1 }, (_, i) => ({
    pos: (i * st / ceil) * 100 + '%', label: num(i * st),
  }))
  const h = hov !== null && hov < cnt.length ? hov : null
  const legend = [
    { bg: 'var(--color-accent-700)', label: `${full}+ days · ${num(nFull)}` },
    { bg: 'var(--color-accent-400)', label: `${low}–${full - 1} days · ${num(ppl.length - nFull - nLow)}` },
    { bg: 'var(--color-neutral-500)', label: `under ${low} days · ${num(nLow)}` },
  ]
  const cols = '44px minmax(0,1fr)'

  return (
    <Sheet style={{ paddingBottom: 22 }}>
      <PanelHead title="Attendance distribution"
        hint={`${num(ppl.length)} people with at least one day present${S.isAll ? '' : ' · ' + S.sel} · median ${med} days`} />
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 8, marginTop: 22 }}>
        <div className="num" style={{
          position: 'relative', height: 240, fontSize: 11, color: 'var(--color-neutral-600)',
        }}>
          {ticks.map((t) => (
            <span key={t.label} style={{
              position: 'absolute', right: 0, bottom: t.pos, transform: 'translateY(50%)',
            }}>{t.label}</span>
          ))}
        </div>
        <div onMouseLeave={() => setHov(null)} style={{
          position: 'relative', height: 240,
          borderLeft: '1px solid var(--color-neutral-500)',
          borderBottom: '1px solid var(--color-neutral-500)',
        }}>
          {ticks.map((t) => (
            <span key={t.label} style={{
              position: 'absolute', left: 0, right: 0, bottom: t.pos,
              borderTop: '1px solid var(--color-neutral-200)',
            }} />
          ))}
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
            gap: 2, padding: '0 2px',
          }}>
            {bars.map((b, i) => (
              <div key={i} onMouseEnter={() => setHov(i)} style={{
                flex: '1 1 0', height: '100%', display: 'flex', alignItems: 'flex-end',
                cursor: 'default',
              }}>
                <div style={{ width: '100%', height: b.h, background: b.bg }} />
              </div>
            ))}
          </div>
          {h !== null ? (
            <div style={{
              position: 'absolute', top: 8, left: ((h + 0.5) / cnt.length) * 100 + '%',
              transform: h / cnt.length > 0.6 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
              pointerEvents: 'none', minWidth: 190, padding: '10px 12px',
              background: 'var(--color-bg)', border: '1px solid var(--color-divider)',
              boxShadow: 'var(--shadow-md)', fontSize: 12, zIndex: 3,
            }}>
              <div style={{ fontWeight: 600 }}>{h + 1} day{h ? 's' : ''} present</div>
              <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>
                {num(cnt[h])} people · {num(ppl.length ? (cnt[h] / ppl.length) * 100 : 0, 1)}% of those on roll
              </div>
              <div style={{ color: 'var(--color-neutral-700)', marginTop: 2 }}>
                {num(ppl.filter((w) => w.p >= h + 1).length)} people present {h + 1}+ days
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 8 }}>
        <div />
        <div className="num" style={{
          display: 'flex', gap: 2, padding: '6px 2px 0', fontSize: 11,
          color: 'var(--color-neutral-700)',
        }}>
          {bars.map((b, i) => <span key={i} style={{ flex: '1 1 0', textAlign: 'center' }}>{b.xl}</span>)}
        </div>
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
        marginTop: 10, fontSize: 12, color: 'var(--color-neutral-700)',
      }}>
        <span>X: days present in the month · Y: number of people</span>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {legend.map((l) => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, display: 'block', flex: 'none', background: l.bg }} />
              {l.label}
            </div>
          ))}
        </div>
      </div>
    </Sheet>
  )
}

// ---------------------------------------------------------- to chase
const ALL = 9999
const GRID = '34px minmax(0,1fr) 170px minmax(120px,1fr) 104px'

// Misses and late days as a share of the worker's own present days.
const mp = (w: AttWorker) => (w.p ? (Math.min(w.m, w.p) / w.p) * 100 : 0)
const lp = (w: AttWorker) => (w.p ? (Math.min(w.l, w.p) / w.p) * 100 : 0)

// Count with share as the tiebreak, or the other way about.
const sorters = (missSort: MissSort, lateSort: MissSort):
  Record<WorkerList, (a: AttWorker, b: AttWorker) => number> => ({
  att: (a, b) => a.att - b.att || b.p - a.p,
  miss: missSort === 'pct'
    ? (a, b) => mp(b) - mp(a) || b.m - a.m
    : (a, b) => b.m - a.m || mp(b) - mp(a),
  late: lateSort === 'pct'
    ? (a, b) => lp(b) - lp(a) || b.l - a.l
    : (a, b) => b.l - a.l || lp(b) - lp(a),
})

function ToChase({ D, S, people, ui, setUi }: Props) {
  const W = words(people)
  const staff = people === 'staff'
  const lists = ([
    { k: 'att', label: 'Low attendance', metric: 'Attendance',
      note: `Ranked by days present out of the days the ${staff ? 'report' : 'register'} lists each ${W.one} — ${num(D.dayCount)} for most, fewer for one who joined or left inside the period. Anyone at nil is marked absent on every day listed: a leaver still live on the ${staff ? 'report' : 'register'}, or a genuine long absence to close out.` },
    { k: 'miss', label: 'Punch misses', metric: 'Misses',
      note: `Punch misses counted only on days the ${W.one} was present; % is misses ÷ present days. Sort by count to find the biggest payroll headaches, or by % to find ${W.many} who miss on most days they come in. A ${W.one} with a high rate is usually a reader or training problem, not a discipline one.` },
    { k: 'late', label: 'Late comers', metric: 'Late share',
      note: `Late days counted only on days the ${W.one} was present; % is late days ÷ present days. Sort by count for the most late arrivals, or by % for habitual latecomers — the % view lists only ${W.many} with at least ten present days so a two-day ${W.one} cannot top it.` },
  ] as { k: WorkerList; label: string; metric: string; note: string }[])
    .filter((l) => !staff || l.k !== 'miss')   // staff have no register to miss against
  const list = lists.some((l) => l.k === ui.list) ? ui.list : 'att'
  const active = lists.find((l) => l.k === list) ?? lists[0]
  const isMissTab = list === 'miss' || list === 'late'
  // Which of the two sorts the control shows and sets.
  const sort = list === 'late' ? ui.lateSort : ui.missSort
  const byPct = isMissTab && sort === 'pct'

  const all = inView(D, S)
  // The % view of the late list keeps to ten present days or more, so a
  // two-day worker cannot top it.
  const pool = list === 'late'
    ? all.filter((w) => w.p > 0 && w.l > 0 && (ui.lateSort === 'n' || w.p >= 10))
    : list === 'miss' ? all.filter((w) => w.p > 0 && w.m > 0) : all
  const sorted = [...pool].sort(sorters(ui.missSort, ui.lateSort)[list])
  const ranked = ui.limit >= ALL ? sorted : sorted.slice(0, ui.limit)
  const value = (w: AttWorker) => (list === 'att' ? w.att
    : list === 'miss' ? (byPct ? mp(w) : w.m) : byPct ? lp(w) : w.l)
  const max = list === 'att' || byPct ? 100 : Math.max(1, ...ranked.map(value))

  return (
    <Sheet>
      <PanelHead title={staff ? 'Staff to chase' : 'Workers to chase'}
        hint={`${S.isAll ? 'All departments' : S.sel} · ${heads(all.length, people)} · whole period, not date-filtered`} />
      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 20, flexWrap: 'wrap', marginTop: 14,
      }}>
        <Segmented<WorkerList> value={list} minWidth={150}
          onChange={(l) => setUi({ ...ui, list: l })}
          options={lists.map((l) => ({ value: l.k, label: l.label }))} />
        {isMissTab ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="axis-title">Sort by</span>
            <Segmented<MissSort> value={sort} minWidth={90}
              onChange={(s) => setUi(list === 'late' ? { ...ui, lateSort: s } : { ...ui, missSort: s })}
              options={[{ value: 'n', label: 'Count' }, { value: 'pct', label: '% of present' }]} />
          </div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="axis-title">Rows</span>
          <Segmented<number> value={ui.limit} minWidth={60}
            onChange={(limit) => setUi({ ...ui, limit })}
            options={[20, 50, 100, ALL].map((n) => ({ value: n, label: n >= ALL ? 'All' : String(n) }))} />
          <span className="num" style={{
            fontSize: 12, color: 'var(--color-neutral-600)', whiteSpace: 'nowrap',
          }}>Showing {num(ranked.length)} of {num(sorted.length)}</span>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: GRID, gap: 14, padding: '16px 4px 8px',
        fontSize: 10, letterSpacing: '0.11em', textTransform: 'uppercase',
        color: 'var(--color-neutral-600)', borderBottom: '1px solid var(--color-divider)',
      }}>
        <div /><div>{W.one}</div><div>Department</div><div>{active.metric}</div>
        <div style={{ textAlign: 'right' }}>Value</div>
      </div>
      {ranked.map((w, i) => {
        const v = value(w)
        const bad = list === 'att' ? w.att < 60
          : list === 'miss' ? (byPct ? mp(w) >= 30 : w.m >= 8) : byPct ? lp(w) >= 50 : w.l >= 15
        return (
          <div key={w.id} style={{
            display: 'grid', gridTemplateColumns: GRID, gap: 14, alignItems: 'center',
            padding: '11px 4px', borderBottom: '1px solid var(--color-divider)',
          }}>
            <div className="num" style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>
              {String(i + 1).padStart(2, '0')}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="truncate" style={{ fontSize: 14 }}>{w.name}</div>
              <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', marginTop: 2 }}>
                {w.id} · {num(w.p)} present of {num(w.listed)} · {num(w.l)} late ({num(lp(w), 0)}%)
                {staff ? '' : ` · ${num(w.m)} punch misses (${num(mp(w), 0)}% of present days)`}
                {w.f ? ` · ${num(w.f)} one-punch` : ''}{w.ot ? ` · ${num(w.ot)} h OT` : ''}
              </div>
            </div>
            <div className="truncate" style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>{w.dept}</div>
            <Track width={100} height={8} inset={1}
              value={Math.round(Math.min(v / max, 1) * 100)} fill={bad ? BAD : 'var(--c-bar1)'} />
            <div className="num" style={{
              fontSize: 14, textAlign: 'right', whiteSpace: 'nowrap',
              color: bad ? BAD : 'var(--color-text)',
            }}>
              {list === 'att' ? num(w.att, 1) + '%'
                : list === 'miss'
                  ? (byPct ? `${num(mp(w), 0)}% · ${num(w.m)} d` : `${num(w.m)} d · ${num(mp(w), 0)}%`)
                  : byPct ? `${num(lp(w), 0)}% · ${num(w.l)} d` : `${num(w.l)} d · ${num(lp(w), 0)}%`}
            </div>
          </div>
        )
      })}
      {ranked.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--color-neutral-600)', padding: '20px 4px' }}>
          No {people} in this department meet the threshold for this list.
        </div>
      ) : null}
      <Foot top={16}>{active.note}</Foot>
    </Sheet>
  )
}

// ----------------------------------------------------------- hygiene
function Hygiene({ D, S, people }: Props) {
  const { isAll, sel, selBase } = S
  const staff = people === 'staff'
  const reg = isAll
    ? { reg: D.register, active: D.activeRoll, never: D.neverPunched }
    : D.deptRegister[sel] ?? { reg: S.activeRoll, active: S.activeRoll, never: 0 }
  const dormant = reg.reg - reg.active
  const of = isAll ? D.totals : selBase
  const where = isAll ? '' : ' in ' + sel

  // Staff have no register, so the tiles that set it against the
  // biometric have nothing to say for them.
  const tiles = staff ? [
    { value: reg.never,
      label: `staff on the report with no punch on any day of ${D.monthName}`,
      note: `Of ${num(reg.reg)} staff on the biometric report${where}, ${num(reg.reg - reg.never)} have at least one punch. The rest are on it by remark alone — absent all month, or marked present with no device record — so their shift and hours are not on file at all.` },
    { value: of?.short ?? 0,
      label: 'half-day and short-hours flags',
      note: 'HALF DAY, EARLY OUT and “n HRS DUTY” on a day the report counts as present. Small enough to chase individually.' },
  ] : [
    { value: reg.never,
      label: `workers on the register who never punched in ${D.monthName}`,
      note: `Of ${num(reg.reg)} workers on the register${where}, ${num(reg.reg - reg.never)} have at least one punch on the biometric. The rest are marked present by hand and read as not being on the device — their shift and hours are not on file at all.`
        + (dormant ? ` A further ${num(dormant)} have a biometric record and no register mark on any day.` : '') },
    { value: of?.absentPunched ?? 0,
      label: 'days marked absent despite a punch',
      note: `A punch exists but the register books the day A — ${num(of?.absentPunched ?? 0)} of ${num(of?.markedA ?? 0)} absent days. Either a short-hours rule is being applied silently or the out-punch is missing; both need a written rule.` },
    { value: of?.miss ?? 0,
      label: 'punch misses',
      note: 'The register marks the worker present and the biometric does not have both punches — none at all, or one of the two. Each one makes the hours worked for that day a guess.' },
    { value: of?.flagged ?? 0,
      label: 'of which one-punch and wrong-punch days',
      note: 'The part of the punch misses the biometric flagged itself — NO IN PUNCH, NO OUT PUNCH, WRONG PUNCH. The rest have no punch at all.' },
    { value: of?.short ?? 0,
      label: 'half-day and short-hours flags',
      note: 'HALF DAY, EARLY OUT and “n HRS DUTY” on a day the register counts as a full present day. Small enough to chase individually. Worth reconciling against the wage register for the month.' },
  ]

  return (
    <Sheet>
      <PanelHead title={staff ? 'Report hygiene' : 'Register hygiene'}
        hint={`${isAll ? 'Whole unit' : sel + ' only'} · whole period`} />
      <Sub>
        {staff
          ? 'What the biometric report leaves open before the numbers can be trusted further'
          : 'Things to fix between the register and the biometric before the numbers can be trusted further'}
      </Sub>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))',
        gap: 16, marginTop: 18,
      }}>
        {tiles.map((h) => (
          <div key={h.label} style={{
            borderLeft: '2px solid var(--color-accent)', padding: '2px 0 2px 14px',
          }}>
            <div className="num" style={{
              fontFamily: 'var(--font-heading)', fontSize: 28, lineHeight: 1,
            }}>{num(h.value)}</div>
            <div style={{ fontSize: 13, marginTop: 5 }}>{h.label}</div>
            <div className="pretty" style={{
              fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 5, lineHeight: 1.45,
            }}>{h.note}</div>
          </div>
        ))}
      </div>
      {isAll && D.unmarkedRows ? (
        <Foot top={18}>
          {staff
            ? `Also on file and in none of the figures: ${num(D.unmarkedRows)} report rows with neither a punch nor a remark, so no mark for the day.`
            : `Also on file and in none of the figures: ${num(D.unmarkedRows)} biometric rows on days the register has no mark for the worker, ${num(D.unmarkedPunched)} of them with a punch.`}
        </Foot>
      ) : null}
    </Sheet>
  )
}
